<template>
	<LayoutScreen background="--background-base" max-width="none" :style="{ '--portal-header-h': `${headerHeight}px` }">
		<template #content>
			<div ref="portalRoot" class="portal">
				<div class="portal__frame">
					<div ref="headerBand" class="portal__header">
						<PortalHeader ref="header" :menu-open="menuOpen" @open-menu="openMenu" />
					</div>
					<LayoutGrid columns="calc(var(--portal-gutter) + 356px) minmax(0, 1fr)" gap="0" align="start">
						<aside class="portal__sidebar">
							<PortalNav :items="navItems" :active-id="activeId" />
							<PortalAppPromotion variant="qr" />
						</aside>
						<main class="portal__content">
							<router-view />
						</main>
					</LayoutGrid>
					<div class="portal__announcer" aria-live="polite">{{ announcement }}</div>
				</div>
			</div>
		</template>
	</LayoutScreen>
	<PortalDrawer :open="menuOpen" :items="navItems" :active-id="activeId" @close="closeMenu" />
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { LayoutGrid, LayoutScreen } from '@simple-life-app/growth-ui-kova'
import { PORTAL_SECTIONS, portalSection, type PortalNavItem } from '@/portal/sections'
import { useUnreadStore } from '@/store/unread'
import PortalAppPromotion from './PortalAppPromotion.vue'
import PortalDrawer from './PortalDrawer.vue'
import PortalHeader from './PortalHeader.vue'
import PortalNav from './PortalNav.vue'

/**
 * The portal shell (KOVA-796): the one screen the four sections render into. The header band with the
 * logo and the signed-in customer (or the menu button on a phone), the persistent desktop sidebar (nav +
 * app promotion with the QR), the mobile side menu, and the `<router-view>` outlet the section routes fill.
 *
 * Responsive contract — docs/architecture/portal.md: the DS's single 1024px breakpoint is a container
 * query, so the shell queries the same width (`container: portal`) that `LayoutGrid` switches on, and the
 * two never disagree. Below it the grid stacks and the sidebar is gone (the side menu takes over); from
 * it on the sidebar column appears and sticks under the header.
 */

const route = useRoute()
const unread = useUnreadStore()

const activeId = computed(() => route.meta.portalSection ?? null)
const navItems = computed<PortalNavItem[]>(() =>
	PORTAL_SECTIONS.map((section) => ({ ...section, unread: section.id === 'messages' && unread.messages })),
)

// What a section change tells assistive tech, since the page itself never reloads: the tab / history title
// (the app's own title back once the shell is left) and a polite announcement of the section that opened.
const baseTitle = document.title
const announcement = ref('')

watch(
	activeId,
	(id, previous) => {
		if (!id) return
		const label = portalSection(id).label
		document.title = `${label} — Kova`
		if (previous !== undefined) announcement.value = label
	},
	{ immediate: true },
)

onBeforeUnmount(() => {
	document.title = baseTitle
})

// The mobile side menu. It is not a history entry: Back / Forward move between sections, and any route
// change closes it (as does a pick in it, even of the section already open), so the browser never has to
// "un-open" a menu (KOVA-796 scope 2). Closing hands the focus back to the menu button, where it left from.
const menuOpen = ref(false)
const header = ref<InstanceType<typeof PortalHeader> | null>(null)

function openMenu() {
	menuOpen.value = true
}

async function closeMenu() {
	if (!menuOpen.value) return
	menuOpen.value = false
	await nextTick()
	header.value?.focusMenuButton()
}

// Any route change closes the menu — a query or hash step through the history too, not only a new path.
watch(() => route.fullPath, closeMenu)

// The screen, not the window, scrolls (LayoutScreen owns it — styles.md Gotchas), so the router's
// `scrollBehavior` is inert here and the shell opens every section from its top itself: Back / Forward
// included (no offsets are kept per history entry). A query or hash change on the same section stays put.
// jsdom has no scrollIntoView, hence the optional call.
const portalRoot = ref<HTMLElement | null>(null)

watch(
	() => route.path,
	() => portalRoot.value?.scrollIntoView?.({ block: 'start' }),
)

// The header band is sticky and the desktop sidebar sticks right under it, filling the rest of the
// viewport (nav at the top, app promotion at the bottom — Figma 13715:74905), so the shell needs the
// band's height. Measured, not assumed: the band grows with the customer's name and the type scale.
const headerBand = ref<HTMLElement | null>(null)
const headerHeight = ref(0)
let headerObserver: ResizeObserver | undefined

onMounted(() => {
	if (!headerBand.value || typeof ResizeObserver === 'undefined') return
	headerObserver = new ResizeObserver(() => {
		headerHeight.value = headerBand.value?.offsetHeight ?? 0
	})
	headerObserver.observe(headerBand.value)
})

onBeforeUnmount(() => headerObserver?.disconnect())
</script>

<style scoped>
/*
 * Figma 13715:74905 (desktop, 1728 wide): header inset 16 → sidebar 356 at x=94 → content 1136 at x=498
 * (a 48 gap) → 94 right. The 94 gutter is 5.44% of the frame, so it scales down with the container and
 * stops at 24. Figma 14254:169712 (mobile, 390): header inset 12, content gutter 20, 28 under the header.
 */
.portal {
	container: portal / inline-size;
}

/*
 * The spacing lives on the frame, not on `.portal`: a container query never styles its own container. The
 * frame also caps the shell at the 1728 design width and centres it — the shell's own cap, not LayoutScreen's
 * `desktop-max-width`: that prop would also name the screen `ob-screen` and switch on the DS "onboarding
 * desktop" type scale, which remaps the 16px body tokens to 18–20px while the portal frames keep 16. So the
 * body stays on the mobile tokens at every width and only the page title takes the DS desktop size, through
 * `--portal-heading` (the DS's own desktop `--heading-h2`).
 */
.portal__frame {
	--portal-gutter: var(--size-20);
	--portal-header-inset: var(--size-12);
	--portal-content-gap: var(--size-28);
	--portal-bottom: var(--size-24);
	--portal-heading: var(--heading-h2);
	max-width: 1728px;
	margin-inline: auto;
}

/*
 * The screen's scroll container is a DS internal with no prop for this: in-page anchors and edge-aligned focus
 * would otherwise land under the sticky header band. The one `:deep()` in the shell (styles.md Gotchas).
 */
:deep(.layout-screen__scroll-area) {
	scroll-padding-top: calc(var(--portal-header-h, 0px) + var(--size-8));
}

/* Visually hidden, and pinned to the top-left: an absolute box left at its static position after the grid
   would sit below the content and stretch the document past the screen's own scroll area. */
.portal__announcer {
	position: absolute;
	top: 0;
	left: 0;
	width: 1px;
	height: 1px;
	margin: -1px;
	padding: 0;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
	border: 0;
}

.portal__header {
	position: sticky;
	top: 0;
	z-index: 2;
	padding: var(--portal-header-inset) var(--portal-header-inset) 0;
	background: var(--background-base);
}

.portal__sidebar {
	display: none;
}

.portal__content {
	min-width: 0;
	padding: var(--portal-content-gap) var(--portal-gutter) var(--portal-bottom);
}

/* The DS breakpoint (DEV-33579): LayoutGrid turns into the two columns at the same container width. */
@container portal (min-width: 1024px) {
	.portal__frame {
		--portal-gutter: clamp(24px, 5.44cqw, 94px);
		--portal-header-inset: var(--size-16);
		--portal-content-gap: var(--size-60);
		/* 54 under the promotion in the frame (1117 − 1063); no size token at 54. */
		--portal-bottom: 54px;
		--portal-heading: normal var(--fw-bold) var(--32) / var(--lh-38) var(--font-default);
	}

	/*
	 * Sticks under the header band and fills the viewport: the nav at the top, the promotion at the bottom.
	 * A short viewport (a 1366×768 laptop under browser chrome) cannot fit both, so the column then scrolls
	 * on its own instead of clipping the promotion.
	 */
	.portal__sidebar {
		position: sticky;
		top: var(--portal-header-h);
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		gap: var(--size-24);
		height: calc(100dvh - var(--portal-header-h));
		padding: var(--portal-content-gap) 0 var(--portal-bottom) var(--portal-gutter);
		overflow-y: auto;
		scrollbar-width: thin;
	}

	.portal__content {
		padding-left: var(--size-48);
	}
}
</style>
